import { useAddProductInfoRequestMutation } from '@api/productRequest.api';
import { TextCodes } from '@api/types/translation/TextCodes';
import { Button, DocumentList, Image, ProductDetailsContext, Text } from '@components';
import { useAppSelector, useToast } from '@hooks';
import { Close } from '@mui/icons-material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  Input,
  TextField,
  styled,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { AppDispatch } from '@store';
import { getModalState, setModalState } from '@store/modal/modal.slice';
import { GlobalStyles } from '@themes/global/GlobalStyles';
import { t } from '@translations';
import { formatBytes } from '@utils/formatBytes.utils';
import { formatPrice } from '@utils/price.utils';
import { useCallback, useContext, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB in bytes

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialogContent-root': {
    padding: theme.spacing(4),
  },
}));

const StyledTextField = styled(TextField)({
  '& legend span': {
    display: 'none',
  },
  '& .MuiOutlinedInput-root': {
    padding: '0 !important',
    height: 'auto',
  },
  '& textarea': {
    height: '91px !important',
    padding: '10.5px 12px',
  },
});

type RequestInfoModalProps = {
  modalId: string;
};

type RequestInfoFormFields = {
  question: string;
  attachments?: File[];
};

export const AddRequestInfoModal: React.FC<RequestInfoModalProps> = ({
  modalId,
}: RequestInfoModalProps) => {
  const modalState = useAppSelector(getModalState(modalId));
  const dispatch = useDispatch<AppDispatch>();
  const { product = {} } = useContext(ProductDetailsContext);
  const theme = useTheme();
  const { toast } = useToast();
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    control,
    formState: { errors },
  } = useForm<RequestInfoFormFields>();
  const closeModalHandler = useCallback(() => {
    dispatch(setModalState({ id: modalId, isVisible: false }));
    reset();
    setAttachments([]);
  }, [dispatch, modalId, reset]);
  const [addProductInfoRequest, { isLoading }] = useAddProductInfoRequestMutation();
  const [attachments, setAttachments] = useState<File[]>([]);

  const onSubmit = async (data: RequestInfoFormFields) => {
    const formData = new FormData();
    formData.append('question', data.question);
    formData.append('productCode', product?.code || '');
    data.attachments?.forEach((file) => {
      formData.append(`Attachments`, file);
    });
    try {
      await addProductInfoRequest(formData).unwrap();
      reset();
      closeModalHandler();
      toast({
        severity: 'success',
        message: t(TextCodes.ProductRequest.Base.Success),
      });
    } catch (error) {
      toast({
        severity: 'error',
        message: t(TextCodes.ProductRequest.Base.Error),
      });
    }
  };

  const onDocumentRemove = (key: number) => {
    const newFiles = [...attachments];
    newFiles.splice(key, 1);
    setAttachments(newFiles);
    setValue('attachments', newFiles);
  };
  const isBreakepointMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <BootstrapDialog
      id={modalId}
      open={modalState.isVisible}
      onClose={closeModalHandler}
      aria-labelledby='create-info-request'
      fullScreen={isBreakepointMobile}
      maxWidth='sm'
      PaperProps={{
        sx: {
          backgroundColor: theme.palette.background.default,
          border: `1px solid ${theme.palette.primary.main}`,
        },
      }}
    >
      <GlobalStyles>
        <DialogTitle
          sx={{
            m: 0,
            p: {
              xs: 2,
              sm: 4,
            },
          }}
        >
          {t(TextCodes.ProductRequest.Info.ModalTitle)}
        </DialogTitle>
        <IconButton
          aria-label='close'
          onClick={closeModalHandler}
          sx={(theme) => ({
            position: 'absolute',
            right: 8,
            top: 8,
            color: theme.palette.grey[900],
          })}
        >
          <Close />
        </IconButton>
        <DialogContent
          sx={{
            pt: '0 !important',
            px: {
              xs: '16px !important',
              sm: '32px !important',
            },
          }}
        >
          <Text sx={{ mb: 4 }}>{t(TextCodes.ProductRequest.Info.Preamble)}</Text>
          <Box
            className='product-order-element'
            display='flex'
            gap={2}
            p={4}
            bgcolor='white'
            border='1px solid black'
          >
            <Box
              className='product-image-cell'
              sx={{
                '& img': {
                  height: '50px',
                  width: '50px',
                },
              }}
            >
              <Image
                image={product?.mainImage}
                className='product-image'
                resizerParams={{ width: 200, format: 'webp' }}
              />
            </Box>
            <div className='product-order-cell'>
              <div className='product-name'>{product?.name}</div>
              <div className='product-id'>{product?.productNo}</div>
            </div>
            <Box display='flex' alignItems='center' className='product-price-cell'>
              <div className='product-price'>{formatPrice(product?.discountPrice)}</div>
            </Box>
          </Box>
          <Box
            component='form'
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              width: '100%',
              mt: 6,
            }}
            noValidate
            autoComplete='off'
            onSubmit={handleSubmit(onSubmit)}
          >
            <StyledTextField
              required
              label={t(TextCodes.ProductRequest.Info.Question)}
              multiline
              maxRows={4}
              {...register('question', { required: true })}
              error={!!errors.question}
            />
            {attachments.length ? (
              <Box sx={{ mt: 2 }}>
                <DocumentList
                  nogrid='true'
                  documents={attachments.map((file) => ({
                    name: file.name,
                    fileSize: formatBytes(file.size),
                  }))}
                  onRemove={onDocumentRemove}
                />
              </Box>
            ) : null}
            <Controller
              control={control}
              name='attachments'
              render={({ field }) => {
                return (
                  <FormControl sx={{ mt: 2 }}>
                    <Box width='100%'>
                      <label htmlFor='attachments' style={{ width: 'inherit', display: 'grid' }}>
                        <Button
                          component='span'
                          startIcon={<CloudUploadIcon />}
                          sx={{
                            backgroundColor: theme.palette.primary.main,
                            ':hover': {
                              backgroundColor: theme.palette.primary.light,
                            },
                          }}
                          variant='primary'
                        >
                          {t(TextCodes.ProductRequest.Base.UploadDocuments)}
                        </Button>
                      </label>
                      <Input
                        onChange={(event) => {
                          const target = event.target as HTMLInputElement;
                          if (target.files) {
                            const newFiles = Array.from(target.files).filter((file) => {
                              if (file.size > MAX_FILE_SIZE) {
                                toast({
                                  severity: 'error',
                                  message: t(
                                    TextCodes.ProductRequest.Base.UploadDocumentsLimitError,
                                    {
                                      fileName: file.name,
                                    }
                                  ),
                                });
                                return false;
                              }
                              return true;
                            });
                            const allFiles = [...attachments, ...newFiles];
                            setAttachments(allFiles);
                            field.onChange(allFiles);
                          }
                          target.value = '';
                        }}
                        type='file'
                        id='attachments'
                        inputProps={{ accept: '.pdf,.jpg,.jpeg,.png', multiple: true }}
                        sx={{ display: 'none' }}
                      />
                    </Box>
                  </FormControl>
                );
              }}
            />
            <Button
              sx={{
                mt: 2,
                backgroundColor: theme.palette.primary.main,
                ':hover': {
                  backgroundColor: theme.palette.primary.light,
                },
              }}
              variant='primary'
              type='submit'
              busy={isLoading}
            >
              {t(TextCodes.ProductRequest.Base.Submit)}
            </Button>
          </Box>
        </DialogContent>
      </GlobalStyles>
    </BootstrapDialog>
  );
};
